import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { showSuccess, showError } from "../../utils/snackbarHelpers";
import { selectBoardId } from '../../redux/Slices/boardIdSlice';
import { fetchBoardMembers } from '../../redux/Slices/boardMembersSlice';
import ZoobbeSelect from '../Global/ZoobbeSelect';

import { togglePopover } from '../../redux/Slices/popoverSlice';
import { fetchMembers, memberInfo } from '../../redux/Slices/memberSlice';
import { config } from '../../config';

import ImagePlaceholder from '../Global/ImagePlaceholder';

import './scss/InviteWorkspace.scss';
import '../Global/scss/MembersSearchResult.scss';
import { fetchWorkspaces } from '../../redux/Slices/workspaceSlice';
import useOutsideClick from '../../hooks/useOutsideClick';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import Spinner from '../Global/Spinner';

import { isValidEmail, debounce, isWorkspaceAdmin, find } from '../../utils/helpers';
import { Close } from '../icons';
import { useWhiteLabel } from '../../context/WhiteLabelContext';


const InviteWorkspace = ({ workspaceId }) => {
    const dispatch = useDispatch();
    const { getAppName } = useWhiteLabel();
    const appName = getAppName();

    const [email, setEmail] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [message, setMessage] = useState(`Join me on ${appName} and let's work together on this board!`);
    const [activeTab, setActiveTab] = useState('members');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedRole, setSelectedRole] = useState({ value: 'member', label: 'Member' });
    const [popoverState, setPopoverState] = useState({ isVisible: false, contentId: null });
    const [openMemberId, setOpenMemberId] = useState(null); // Track which member is open
    const [loadingUsers, setLoadingUsers] = useState({});

    const [inviteLink, setInviteLink] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchingUsers, setSearchingUsers] = useState(false);

    const [size] = useState(35);

    const [isFocused, setIsFocused] = useState(false);
    const emailRef = useRef();

    // Confirmation state for admin downgrade
    const [showDowngradeConfirmation, setShowDowngradeConfirmation] = useState(false);
    const [pendingRoleChange, setPendingRoleChange] = useState(null);
    const emailInputRef = useRef(null);

    const { user } = useSelector((state) => state.user);
    const { workspaces } = useSelector(state => state.workspaces);

    const boardId = useSelector(selectBoardId);

    const collaborators = useSelector(state => state.member.workspaceMembers);
    const totalAdmins = collaborators.filter(m => m.role === 'admin').length;

    const { handlePopoverClick } = useHandlePopoverClick();

    // Get workspace and check if current user is admin
    const workspace = find.get(workspaces, { name: 'workspace', workspace: workspaceId }) || {};
    const currentUserId = user?.user?._id;
    const currentUserIsAdmin = isWorkspaceAdmin(workspace, currentUserId);

    useEffect(() => {
        if (emailInputRef.current) {
            emailInputRef.current.focus();
        }
    }, []);


    // Debounced search function
    const searchUsers = useRef(
        debounce(async (searchQuery) => {
            if (!searchQuery || searchQuery.trim().length === 0) {
                setSearchResults([]);
                setSearchingUsers(false);
                return;
            }

            setSearchingUsers(true);
            try {
                const response = await fetch(
                    `${config.API_URI}/v1/users/all-users?search=${encodeURIComponent(searchQuery)}&limit=10`,
                    {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                        },
                    }
                );

                if (response.ok) {
                    const data = await response.json();
                    const users = data.users || [];

                    // Show email as an invite option if no users are found and input is a valid email format
                    if (users.length === 0 && isValidEmail(searchQuery)) {
                        setSearchResults([{ email: searchQuery, isEmailInvite: true }]);
                    } else {
                        setSearchResults(users);
                    }
                } else {
                    setSearchResults([]);
                }
            } catch (error) {
                console.error('Error searching users:', error);
                setSearchResults([]);
            } finally {
                setSearchingUsers(false);
            }
        }, 300)
    ).current;

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);

        if (value?.length > 0) {
            searchUsers(value);
        } else {
            setSearchResults([]);
            setSearchingUsers(false);
        }
    };

    const removeUser = (index) => {
        setSelectedUsers((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSelectUser = (user) => {
        if (!selectedUsers.some((selected) => selected._id === user._id)) {
            // If the selected option is an existing user
            setSelectedUsers([...selectedUsers, user]);
        }
        else {
            setSelectedUsers([...selectedUsers, { email: user.email, isEmailInvite: true }]);
        }
        setEmail('');
        setSearchResults([]);
    };

    const handleInviteByEmail = async (e, selectedNonWorkspaceMembers) => {
        e.preventDefault();

        if (selectedNonWorkspaceMembers.length === 0) {
            setLoading(false);
            showError(dispatch, 'Please select at least one user');
            return;
        }

        const memberId = selectedNonWorkspaceMembers[0]._id; // Assuming you're sending one invitee for re-invite

        setLoadingUsers(prev => ({ ...prev, [memberId]: true }));

        try {
            
            const invitees = selectedNonWorkspaceMembers.map((user) => ({
                email: user.email,
                role: selectedRole.value || 'member',
            }));

            const response = await fetch(`${config.API_URI}/v1/workspace/${workspaceId}/invite`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                },
                credentials: 'include',
                body: JSON.stringify({ invitees, message }),
            });

            const data = await response.json();

            if (response.ok) {
                dispatch(fetchMembers({ type: 'workspace', id: workspaceId }));
                dispatch(fetchMembers({ type: 'guests', id: workspaceId }));
                showSuccess(dispatch, 'Invitations sent!');
                setSelectedUsers([]);
                setLoading(false);
            } else {
                showError(dispatch, data.message);
            }
        } catch (error) {
            showError(dispatch, 'Failed to send invite');
        } finally {
            setLoadingUsers(prev => ({ ...prev, [memberId]: false }));
        }
    };

    const handleGenerateJoinLink = async () => {
        try {
            

            const response = await fetch(`${config.API_URI}/v1/workspace/${workspaceId}/generate-join-link`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                },
                credentials: 'include',
            });

            const data = await response.json();
            if (response.ok) setInviteLink(data.joinLink);
            else showError(dispatch, data.message);
        } catch (error) {
            showError(dispatch, 'Failed to generate join link');
        }
    };

    const handleDeleteJoinLink = async () => {
        try {
            

            const response = await fetch(`${config.API_URI}/v1/workspace/${workspaceId}/delete-join-link`, {
                method: 'DELETE',
                headers: {
                    
                },
                credentials: 'include',
            });

            if (response.ok) {
                setInviteLink(null);
                showSuccess(dispatch, 'Join link deleted');
            } else {
                const data = await response.json();
                showError(dispatch, data.message);
            }
        } catch (error) {
            showError(dispatch, 'Failed to delete join link');
        }
    };

    const handleCopyLink = () => {
        if (inviteLink) {
            navigator.clipboard.writeText(inviteLink);
            showSuccess(dispatch, 'Join link copied to clipboard!');
        }
    };

    const addMemberToWorkspace = async (e) => {
        e.preventDefault();

        setLoading(true);
        if (!workspaceId) {
            showError(dispatch, 'Workspace Id Empty');
            return;
        }

        try {
            

            // Prepare the members array with both memberId and role
            const members = selectedUsers.map((user) => ({
                memberId: user._id || null,
                email: user.email || null,
                role: selectedRole.value || 'member',
            }));


            const response = await fetch(`${config.API_URI}/v1/workspace/${workspaceId}/member`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                },
                credentials: 'include',
                body: JSON.stringify({ workspaceId, members, message }),

            });

            const data = await response.json();
            const messageType = response.status === 200 ? 'success' : 'error';

            if (messageType === 'success') {
                showSuccess(dispatch, data.message);
            } else {
                showError(dispatch, data.message);
            }
            if (response.status === 200) {
                setSelectedUsers([]);
                dispatch(fetchMembers({ type: 'workspace', id: workspaceId }));
                dispatch(fetchMembers({ type: 'guests', id: workspaceId }));
            }
            setLoading(false);

        } catch (error) {
            setLoading(false);

            console.error('Error adding member:', error);
            showError(dispatch, 'Error adding member');
        }
    };

    const updateMemberRole = async (newRole, member, confirmSelfDowngrade = false) => {

        const { _id } = member;

        if (!workspaceId || !_id) {
            showError(dispatch, 'Workspace Id or Member Id is empty');
            return;
        }

        try {
            // Prepare the request payload
            const payload = {
                memberId: _id, // ID of the member to update
                newRole: newRole,
                workspaceId: workspaceId,
                confirmSelfDowngrade: confirmSelfDowngrade
            };

            const response = await fetch(`${config.API_URI}/v1/workspace/${workspaceId}/member`, {
                method: 'PUT', // Use PUT method for role update
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            // Check if confirmation is required
            if (response.status === 400 && data.requiresConfirmation && data.isAdminDowngrade) {
                // Show confirmation screen
                setPendingRoleChange({ newRole, member });
                setShowDowngradeConfirmation(true);
                return;
            }

            const messageType = response.status === 200 ? 'success' : 'error';

            if (messageType === 'success') {
                showSuccess(dispatch, data.message);
            } else {
                showError(dispatch, data.message);
            }
            if (response.status === 200) {
                dispatch(fetchMembers({ type: 'workspace', id: workspaceId }));
            }
        } catch (error) {
            console.error('Error updating member role:', error);
            showError(dispatch, 'Error updating member role');
        }
    };

    // Handle confirmation of admin downgrade
    const handleConfirmDowngrade = () => {
        if (pendingRoleChange) {
            updateMemberRole(pendingRoleChange.newRole, pendingRoleChange.member, true);
            setShowDowngradeConfirmation(false);
            setPendingRoleChange(null);
        }
    };

    // Handle cancellation of admin downgrade
    const handleCancelDowngrade = () => {
        setShowDowngradeConfirmation(false);
        setPendingRoleChange(null);
    };

    const handleRoleSelect = (role) => {
        setSelectedRole(role);
    };

    const handleMemberInfo = (e, contentId, member) => {
        const target = e.currentTarget;
        const rect = target.getBoundingClientRect();
        const position = { top: rect.bottom, left: rect.left };

        if (popoverState.isVisible && popoverState.contentId === contentId) {
            setPopoverState({ isVisible: false, contentId: null });
            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
        } else {
            setPopoverState({ isVisible: true, contentId });
            dispatch(togglePopover({ contentId, position, targetId: target.id }));
            dispatch(memberInfo({ member }));
        }
    };

    const roleOptions = [
        { value: 'member', label: 'Member' },
        // { value: 'observer', label: 'Observer' },
        { value: 'admin', label: 'Admin' }
    ];

    const handleFocus = () => {
        setIsFocused(true); // Set focus to true
    };

    useOutsideClick(emailRef, () => setIsFocused(false));

    return (
        <div className="sbm-invite-workspace-modal">
            <div className="sbm-header">
                <h3>Invite Workspace</h3>
            </div>
            {!currentUserIsAdmin && (
                <div className="sbm-no-permission-message" style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '48px', display: 'block', marginBottom: '10px' }}>
                        lock
                    </span>
                    <p>Only workspace admins can invite members to this workspace.</p>
                </div>
            )}
            {currentUserIsAdmin && (
                <form onSubmit={addMemberToWorkspace}>
                <div className="sbm-input-group">
                    <div
                        className="sbm-email-input"
                        ref={emailRef}
                        style={{
                            outline: isFocused ? '1px solid var(--brand-color)' : '1px solid var(--input-border-color)',
                        }}
                    >
                        {selectedUsers.map((selectedUser, index) => (
                            <div key={index} className="sbm-selected-email">
                                {`${selectedUser.name || selectedUser.email}`}
                                <button type="button" onClick={() => removeUser(index)}>
                                    <Close size={16} />
                                </button>
                            </div>
                        ))}
                        <input
                            ref={emailInputRef}
                            type="text"
                            value={email}
                            onChange={handleEmailChange}
                            placeholder="Type name or email"
                            autoComplete="off"
                            autoCapitalize="none"
                            spellCheck="false"
                            onFocus={handleFocus}
                        />
                        {
                            searchingUsers ? (
                                <div className="sbm-search-results">
                                    <div className='no-user-message' style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Spinner size={16} color='var(--text-color)' strokeWidth={7} />
                                        Searching...
                                    </div>
                                </div>
                            ) : searchResults.length > 0 ? (
                                <div className="sbm-search-results">
                                    {searchResults.map((suggestion, index) => (
                                        <div
                                            key={index}
                                            className="mention-suggestion"
                                            onClick={() => handleSelectUser(suggestion)}
                                        >
                                            {suggestion.isEmailInvite ? (
                                                <>
                                                    <ImagePlaceholder key={suggestion._id} size={size} text={suggestion.email} />
                                                    <div>
                                                        <div className="suggession-name">{suggestion.email}</div>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    {suggestion.profilePicture ? (
                                                        <img src={suggestion.profilePicture} alt={suggestion.name} loading="lazy" />
                                                    ) : (
                                                        <ImagePlaceholder key={suggestion._id} size={size} text={suggestion.username} onlineIndicator={false} />
                                                    )}
                                                    <div>
                                                        <div className="suggession-name">{suggestion.name}</div>
                                                        <div className="suggession-handler">{`@${suggestion.username}`}</div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                // Show the "no user found" message if no results and no valid email
                                email && !searchingUsers && !isValidEmail(email) && (
                                    <div className="sbm-search-results">
                                        <div className='no-user-message'>
                                            Looks like that person isn't a {appName} member yet. Add their email address to invite them.
                                        </div>
                                    </div>
                                )
                            )
                        }



                    </div>
                    <ZoobbeSelect
                        options={roleOptions}
                        defaultSelectedOption={0}
                        onSelect={handleRoleSelect}
                    />
                </div>
                <button type="submit" className={`sbm-invite-button${loading ? ' disabled' : ''}`} disabled={loading}>
                    {loading ? <Spinner size={20} color='#fff' strokeWidth={7} /> : 'Share'}
                </button>
            </form>
            )}
            {currentUserIsAdmin && (
                <>
                    <textarea
                        className="sbm-message-input"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                    />
                    <div className="sbm-invite-link">
                        {inviteLink ? (
                            <div className="invite-link-info">
                                <span className="material-symbols-outlined">
                                    link
                                </span>
                                <span className="invite-link-text">Anyone with the link can join as a member</span>
                                <span className="invite-link-actions">
                                    <button onClick={handleCopyLink} className="link-action">Copy link</button> •
                                    <button onClick={handleDeleteJoinLink} className="link-action">Delete link</button>
                                </span>
                            </div>
                        ) : (
                            <div className="invite-link-info">
                                <span className="material-symbols-outlined">
                                    link
                                </span>
                                <span className="invite-link-text">Share this board with a link</span>
                                <span className="invite-link-actions">
                                    <button onClick={handleGenerateJoinLink} className="link-action">Create link</button>
                                </span>
                            </div>
                        )}
                    </div>
                </>
            )}
            <div className="sbm-tabs">
                <button
                    className={`sbm-tab ${activeTab === 'members' ? 'active' : ''}`}
                    onClick={() => setActiveTab('members')}
                >
                    Workspace members
                </button>
                <button
                    className={`sbm-tab ${activeTab === 'requests' ? 'active' : ''}`}
                    onClick={() => setActiveTab('requests')}
                >
                    Requests
                </button>
            </div>

            {activeTab === 'members' && (
                <div className="sbm-workspace-members">
                    {collaborators?.map((member, index) => {

                        if (!member?._id) return;

                        const isCurrentUser = member._id === user?.user?._id;

                        return (
                            <div className="sbm-member" key={index + ' ' + member._id}>
                                <span className="avatar" onClick={(e) => { handleMemberInfo(e, 'memberInfo', member) }} data-popover-trigger>
                                    {member.profilePicture ? (
                                        <img
                                            className="sbm-member-image"
                                            style={{ width: size, height: size }}
                                            src={member.profilePicture}
                                            alt={member.name}
                                            loading="lazy"
                                        />
                                    ) : (
                                        <ImagePlaceholder member={member} size={size} text={member.username} onlineIndicator={false} />
                                    )}
                                </span>
                                <div className="shared-members-name">
                                    <span className="sbm-member-name">
                                        {member.name} {isCurrentUser && '(you)'}
                                    </span>

                                    <span className="member-username">@{member.username} • WorkSpace {member.workspaceRole || member.role}</span>

                                </div>

                                {currentUserIsAdmin ? (
                                    <div
                                        className={`members-options ${openMemberId === member._id ? 'active' : ''}`}
                                        id={`popover-invite-member-${member._id}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenMemberId(openMemberId === member._id ? null : member._id);
                                            handlePopoverClick(e, 'memberRole', {
                                                memberId: member._id,
                                                currentRole: member.role,
                                                isClosed: false,
                                                boardId,
                                                memberDeleteTarget: `popover-member-${member._id}`, // Pass ID instead of the event
                                                totalAdmins,
                                                apiPath: 'workspace',
                                                typeId: workspaceId,

                                            });
                                        }}
                                        data-popover-trigger
                                    >
                                        {member.role}
                                        <span className="material-symbols-outlined">keyboard_arrow_down</span>
                                    </div>
                                ) : (
                                    <span className="member-role-display" style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>
                                        {member.role}
                                    </span>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
            {activeTab === 'requests' && (
                <div className="sbm-requests">No requests found</div>
            )}

            {/* Confirmation dialog for admin downgrade */}
            {showDowngradeConfirmation && (
                <div className="confirm-dialog-overlay">
                    <div className="confirm-dialog">
                        <h4>Change Role?</h4>
                        <p>
                            You are about to remove your admin privileges. You will no longer be able to manage workspace members, change roles, or invite new members.
                        </p>
                        <p style={{ fontWeight: 500, marginTop: '8px' }}>
                            Are you sure you want to continue?
                        </p>
                        <div className="dialog-actions">
                            <button
                                className="cancel-button"
                                onClick={handleCancelDowngrade}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-button"
                                onClick={handleConfirmDowngrade}
                            >
                                Change Role
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InviteWorkspace;
